import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { OrgProvider, useOrg } from "./auth/OrgProvider";
import { RequireAuth, RedirectIfAuthed } from "./auth/RequireAuth";
import { DashboardLayout } from "./layout/DashboardLayout";
import { DashboardPage } from "./pages/DashboardPage";
import { CustomersPage } from "./pages/CustomersPage";
import { PropertiesPage } from "./pages/PropertiesPage";
import { ServicesPage } from "./pages/ServicesPage";
import { LeadsPage } from "./pages/LeadsPage";
import { LoginPage } from "./pages/LoginPage";
import { SignupPage } from "./pages/SignupPage";
import { OnboardingPage } from "./pages/OnboardingPage";
import { QuotePage } from "./pages/QuotePage";
import { NotFoundPage } from "./pages/NotFoundPage";

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <OrgProvider>
          <Routes>
            <Route path="/quote" element={<QuotePage />} />
            <Route
              path="/login"
              element={
                <RedirectIfAuthed>
                  <LoginPage />
                </RedirectIfAuthed>
              }
            />
            <Route
              path="/signup"
              element={
                <RedirectIfAuthed>
                  <SignupPage />
                </RedirectIfAuthed>
              }
            />
            <Route
              path="/onboarding"
              element={
                <RequireAuth>
                  <OnboardingPage />
                </RequireAuth>
              }
            />
            <Route
              path="/"
              element={
                <RequireAuth>
                  <OrgGate>
                    <DashboardLayout />
                  </OrgGate>
                </RequireAuth>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="leads" element={<LeadsPage />} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="properties" element={<PropertiesPage />} />
              <Route path="services" element={<ServicesPage />} />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </OrgProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

/**
 * Send authed users with no memberships to onboarding.
 * When Supabase isn't configured we let them through to the dashboard shell
 * so the layout is still inspectable without a backend.
 */
function OrgGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { memberships, loading } = useOrg();
  if (!user) return <>{children}</>;
  if (loading) return null;
  if (memberships.length === 0) return <Navigate to="/onboarding" replace />;
  return <>{children}</>;
}
